import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useUser } from '../../context/UserContext';
import { useNotification } from '../../context/NotificationContext';
import LoadingOverlay from '../ui/LoadingOverlay';
import { auth } from '../../firebaseConfig';
import api from '../../api/api';
import styles from './RegistrationWizard.module.css';

const RegistrationWizard = ({ programId, config, template, existingApplication, onClose, onSubmit }) => {
  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState({
    personalInfo: {},
    emergencyContact: {},
    healthInfo: {},
    parentGuardian: {},
    soccerExperience: {},
    medicalClearance: {},
    programSpecific: {},
    customFields: {}
  });
  const [uploadedDocuments, setUploadedDocuments] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const { userProfile } = useUser();
  const { showSuccess, showError } = useNotification();

  const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:5000';

  // Debug: Log template data and clean up duplicates
  useEffect(() => {
    
    // Log duplicate field analysis
    if (template?.sections) {
      template.sections.forEach(section => {
        if (section.fields) {
          const fieldCounts = {};
          section.fields.forEach(field => {
            fieldCounts[field.id] = (fieldCounts[field.id] || 0) + 1;
          });
          
          const duplicates = Object.entries(fieldCounts).filter(([id, count]) => count > 1);
          if (duplicates.length > 0) {
            console.warn(`Section "${section.name}" has duplicate fields:`, duplicates);
          }
        }
      });
    }
  }, [template, config]);

  // Generate dynamic steps based on template
  const generateStepsFromTemplate = () => {
    const dynamicSteps = [];
    let stepId = 1;

    // Template processing started

    if (!template || !template.sections) {
      // Fallback to default steps if no template
      return [
        { id: 1, name: 'Personal Information', icon: '👤', sectionId: 'personal_info' },
        { id: 2, name: 'Emergency Contact', icon: '🆘', sectionId: 'emergency_contact' },
        { id: 3, name: 'Health Information', icon: '🏥', sectionId: 'health_info' },
        { id: 4, name: 'Document Upload', icon: '📎', sectionId: 'documents' },
        { id: 5, name: 'Review & Submit', icon: '✅', sectionId: 'review' }
      ];
    }

    // Create steps from template sections
    template.sections.forEach(section => {
      const stepIcons = {
        'personal_info': '👤',
        'emergency_contact': '🆘',
        'health_info': '🏥',
        'parent_guardian': '👨‍👩‍👧‍👦',
        'soccer_experience': '⚽',
        'medical_clearance': '🏥'
      };

      // Clean up duplicate fields in the section
      const cleanedSection = {
        ...section,
        fields: section.fields ? (() => {
          const uniqueFields = [];
          const seenFieldIds = new Set();
          
          section.fields.forEach(field => {
            if (!seenFieldIds.has(field.id)) {
              seenFieldIds.add(field.id);
              uniqueFields.push(field);
            }
          });
          
          // Section cleaned successfully
          return uniqueFields;
        })() : []
      };

      dynamicSteps.push({
        id: stepId++,
        name: section.name,
        icon: stepIcons[section.id] || '📝',
        sectionId: section.id,
        section: cleanedSection
      });
    });

    // Always add document upload step if there are document requirements
    if (template.documentRequirements && template.documentRequirements.length > 0) {
      dynamicSteps.push({
        id: stepId++,
        name: 'Document Upload',
        icon: '📎',
        sectionId: 'documents'
      });
    }

    // Always add review step
    dynamicSteps.push({
      id: stepId++,
      name: 'Review & Submit',
      icon: '✅',
      sectionId: 'review'
    });

    return dynamicSteps;
  };

  const steps = generateStepsFromTemplate();

  // Pre-fill form data from existing application or user profile
  useEffect(() => {
    // Check for different possible form data properties
    const formDataSource = existingApplication?.formData || 
                          existingApplication?.data || 
                          existingApplication?.applicationData ||
                          existingApplication?.form;
    
    if (formDataSource) {
      // Pre-fill from existing application data (including rejected applications for convenience)
      setFormData(prev => ({
        ...prev,
        ...formDataSource
      }));
      
      // Pre-fill uploaded documents if any (but only for draft applications, not rejected)
      if (existingApplication.documents && existingApplication.status !== 'rejected') {
        const documentsMap = {};
        existingApplication.documents.forEach(doc => {
          if (doc.files && doc.files.length > 0) {
            documentsMap[doc.requirementId] = doc.files;
          }
        });
        setUploadedDocuments(documentsMap);
      }
    } else if (userProfile) {
      // Pre-fill from user profile
      setFormData(prev => ({
        ...prev,
        personalInfo: {
          firstName: userProfile.name?.split(' ')[0] || '',
          lastName: userProfile.name?.split(' ').slice(1).join(' ') || '',
          email: userProfile.email || '',
          age: userProfile.age || '',
          location: userProfile.location || '',
          nationality: userProfile.nationality || '',
          phone: userProfile.phone || '',
          // Pre-fill from player profile
          height: userProfile.height || '',
          weight: userProfile.weight || '',
          position: userProfile.position || '',
          yearsExperience: userProfile.yearsExperience || ''
        }
      }));
    }
  }, [userProfile, existingApplication]);

  const handleInputChange = (section, field, value) => {
    setFormData(prev => ({
      ...prev,
      [section]: {
        ...prev[section],
        [field]: value
      }
    }));
  };

  // Step-specific validation
  const validateCurrentStep = () => {
    const errors = [];
    const currentStepData = steps[currentStep - 1];
    
    if (!currentStepData) return errors;

    // Handle special steps
    if (currentStepData.sectionId === 'documents') {
      // Validate required documents
      if (template?.documentRequirements) {
        template.documentRequirements.forEach(doc => {
          if (doc.required && !uploadedDocuments[doc.id]) {
            errors.push(`${doc.name} is required`);
          }
        });
      }
      return errors;
    }

    if (currentStepData.sectionId === 'review') {
      // Review step validation is handled by overall form validation
      return errors;
    }

    // Validate dynamic section fields
    if (currentStepData.section) {
      const section = currentStepData.section;
      const sectionKey = getSectionKey(section.id);
      const sectionData = formData[sectionKey] || {};

      section.fields?.forEach(field => {
        if (field.required) {
          const formFieldKey = getFormFieldKey(field.id);
          const value = sectionData[formFieldKey];
          
          if (!value || (typeof value === 'string' && !value.trim())) {
            errors.push(`${field.label} is required`);
          } else if (field.type === 'email' && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
            errors.push(`Valid ${field.label} is required`);
          }
        }
      });
    }
    
    return errors;
  };

  const handleNext = () => {
    // Validate current step before proceeding
    const stepErrors = validateCurrentStep();
    if (stepErrors.length > 0) {
      showError(`Please complete the required fields in this step:\n• ${stepErrors.join('\n• ')}`, {
        duration: 6000
      });
      return;
    }
    
    if (currentStep < steps.length) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handlePrevious = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  // Validation function to check required fields
  const validateFormData = () => {
    const errors = [];
    
    if (!template?.sections) {
      return errors; // No validation if no template
    }

    // Validate all template sections (deduplicate by section ID)
    const validatedSections = new Set();
    template.sections.forEach(section => {
      if (validatedSections.has(section.id)) {
        return; // Skip duplicate sections
      }
      validatedSections.add(section.id);
      
      const sectionKey = getSectionKey(section.id);
      const sectionData = formData[sectionKey] || {};

      section.fields?.forEach(field => {
        if (field.required) {
          const formFieldKey = getFormFieldKey(field.id);
          const value = sectionData[formFieldKey];
          
          if (!value || (typeof value === 'string' && !value.trim())) {
            errors.push(`${field.label} is required`);
          } else if (field.type === 'email' && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
            errors.push(`Valid ${field.label} is required`);
          }
        }
      });
    });
    
    // Document validation
    if (template?.documentRequirements) {
      template.documentRequirements.forEach(doc => {
        if (doc.required && !uploadedDocuments[doc.id]) {
          errors.push(`${doc.name} is required`);
        }
      });
    }
    
    return errors;
  };

  // Check if form is complete for submit button state
  const isFormComplete = () => {
    const errors = validateFormData();
    return errors.length === 0 && termsAccepted;
  };

  // Check if a specific step is complete
  const isStepComplete = (stepId) => {
    const stepErrors = validateStepById(stepId);
    return stepErrors.length === 0;
  };

  // Validate a specific step by ID
  const validateStepById = (stepId) => {
    const errors = [];
    const stepData = steps[stepId - 1];
    
    if (!stepData) return errors;

    // Handle special steps
    if (stepData.sectionId === 'documents') {
      if (template?.documentRequirements) {
        template.documentRequirements.forEach(doc => {
          if (doc.required && !uploadedDocuments[doc.id]) {
            errors.push(`${doc.name} is required`);
          }
        });
      }
      return errors;
    }

    if (stepData.sectionId === 'review') {
      // Review step is complete if all other steps are complete
      return errors;
    }

    // Validate dynamic section fields
    if (stepData.section) {
      const section = stepData.section;
      const sectionKey = getSectionKey(section.id);
      const sectionData = formData[sectionKey] || {};

      section.fields?.forEach(field => {
        if (field.required) {
          const formFieldKey = getFormFieldKey(field.id);
          const value = sectionData[formFieldKey];
          
          if (!value || (typeof value === 'string' && !value.trim())) {
            errors.push(`${field.label} is required`);
          } else if (field.type === 'email' && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
            errors.push(`Valid ${field.label} is required`);
          }
        }
      });
    }
    
    return errors;
  };

  const handleSubmit = async () => {
    // Check terms acceptance first
    if (!termsAccepted) {
      showError('You must agree to the terms and conditions and liability waiver to submit your application.', {
        duration: 5000
      });
      return;
    }

    // Validate form before submission
    const validationErrors = validateFormData();
    if (validationErrors.length > 0) {
      showError(`Please complete all required fields:\n• ${validationErrors.join('\n• ')}`, {
        duration: 8000
      });
      return;
    }

    setIsSubmitting(true);
    try {
      // Create FormData to handle both form data and files
      const formDataObj = new FormData();
      
      // Add form data as JSON string
      formDataObj.append('formData', JSON.stringify(formData));
      
      // Add multiple flags to indicate this is a final submission (not a draft save)
      formDataObj.append('isSubmission', 'true');
      formDataObj.append('submit', 'true');
      formDataObj.append('status', 'submitted');
      
      // Add files individually
      Object.entries(uploadedDocuments).forEach(([key, fileOrFiles]) => {
        if (fileOrFiles instanceof File) {
          formDataObj.append(`documents[${key}]`, fileOrFiles);
        } else if (Array.isArray(fileOrFiles)) {
          // Handle multiple files (like insurance cards)
          fileOrFiles.forEach((file, index) => {
            if (file instanceof File) {
              formDataObj.append(`documents[${key}][${index}]`, file);
            }
          });
        }
      });

      // Call the onSubmit callback with the FormData (the hook will handle the API call)
      const result = await onSubmit(formDataObj);
      
      // Show success notification
      const applicationNumber = result?.application?.applicationNumber || 'N/A';
      let successMessage = `Registration application submitted successfully! Application #: ${applicationNumber}`;
      
      // Show special message for new applications after rejection
      if (result?.isNewApplicationAfterRejection) {
        successMessage = `New application submitted successfully! Application #: ${applicationNumber}`;
      } else if (result?.isResubmission) {
        successMessage = `Application resubmitted successfully! Application #: ${applicationNumber}`;
      }
      
      showSuccess(successMessage, {
        duration: 8000
      });
      
      // Close the wizard
      onClose();
    } catch (error) {
      console.error('Error submitting application:', error);
      
      // Enhanced error handling for specific scenarios
      let errorMessage = 'Please try again.';
      
      if (error.message) {
        if (error.message.includes('File too large')) {
          errorMessage = 'One or more files are too large. Maximum size is 10MB per file.';
        } else if (error.message.includes('Invalid file type')) {
          errorMessage = 'Invalid file type uploaded. Please use PDF, JPG, PNG, DOC, or DOCX files only.';
        } else if (error.message.includes('validation failed')) {
          errorMessage = 'Please check that all required fields are filled out correctly.';
        } else if (error.message.includes('duplicate') || error.message.includes('already exists')) {
          errorMessage = 'You have already submitted an application for this program.';
        } else if (error.message.includes('401') || error.message.includes('Unauthorized')) {
          errorMessage = 'Your session has expired. Please log in again.';
        } else if (error.message.includes('403') || error.message.includes('Forbidden')) {
          if (error.message.includes('Only the original applicant')) {
            errorMessage = 'Only the original applicant can resubmit this application.';
          } else {
            errorMessage = 'You do not have permission to apply for this program.';
          }
        } else if (error.message.includes('404') || error.message.includes('Not Found')) {
          errorMessage = 'Program not found. Please check the program details.';
        } else {
          errorMessage = error.message;
        }
      }
      
      // Show error notification
      showError(`Error submitting application: ${errorMessage}`, {
        duration: 10000
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle overlay click to close modal (click outside modal content)
  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  // File validation to match backend requirements
  const validateFile = (file) => {
    const allowedFileTypes = ['.pdf', '.jpg', '.jpeg', '.png', '.doc', '.docx'];
    const allowedMimeTypes = [
      'application/pdf',
      'image/jpeg',
      'image/jpg', 
      'image/png',
      'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
    ];
    const maxFileSize = 10 * 1024 * 1024; // 10MB

    const ext = file.name.substring(file.name.lastIndexOf('.')).toLowerCase();
    
    if (!allowedFileTypes.includes(ext)) {
      throw new Error(`Invalid file type. Only ${allowedFileTypes.join(', ')} files allowed.`);
    }
    
    if (!allowedMimeTypes.includes(file.type.toLowerCase())) {
      throw new Error('Invalid file format.');
    }
    
    if (file.size > maxFileSize) {
      throw new Error('File too large. Maximum size is 10MB.');
    }
    
    return true;
  };

  // Helper function to render form fields dynamically
  const renderFormField = (field, sectionKey) => {
    const formFieldKey = getFormFieldKey(field.id);
    const value = formData[sectionKey]?.[formFieldKey] || '';
    
    switch (field.type) {
      case 'text':
      case 'email':
      case 'tel':
      case 'date':
        return (
          <div className={styles.inputGroup}>
            <label>
              {field.label} {field.required && '*'}
            </label>
            <input
              type={field.type}
              value={value}
              onChange={(e) => handleInputChange(sectionKey, formFieldKey, e.target.value)}
              placeholder={field.placeholder}
              required={field.required}
            />
          </div>
        );
      
      case 'textarea':
        return (
          <div className={styles.inputGroup}>
            <label>
              {field.label} {field.required && '*'}
            </label>
            <textarea
              value={value}
              onChange={(e) => handleInputChange(sectionKey, formFieldKey, e.target.value)}
              placeholder={field.placeholder}
              required={field.required}
              rows={3}
            />
          </div>
        );
      
      case 'select':
        return (
          <div className={styles.inputGroup}>
            <label>
              {field.label} {field.required && '*'}
            </label>
            <select
              value={value}
              onChange={(e) => handleInputChange(sectionKey, formFieldKey, e.target.value)}
              required={field.required}
            >
              <option value="">Select {field.label}</option>
              {field.options?.map(option => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
        );
      
      case 'checkbox':
        return (
          <div className={styles.checkboxGroup}>
            <label className={styles.checkbox}>
              <input
                type="checkbox"
                checked={value || false}
                    onChange={(e) => handleInputChange(sectionKey, formFieldKey, e.target.checked)}
              />
              <span>{field.label}</span>
            </label>
          </div>
        );
      
      case 'radio':
        return (
          <div className={styles.inputGroup}>
            <label>{field.label} {field.required && '*'}</label>
            <div className={styles.radioGroup}>
              {field.options?.map(option => (
                <label key={option.value} className={styles.radio}>
                  <input
                    type="radio"
                    name={field.id}
                    value={option.value}
                    checked={value === option.value}
                    onChange={(e) => handleInputChange(sectionKey, formFieldKey, e.target.value)}
                    required={field.required}
                  />
                  <span>{option.label}</span>
                </label>
              ))}
            </div>
          </div>
        );
      
      default:
        return null;
    }
  };

  // Helper function to get section key from section ID
  const getSectionKey = (sectionId) => {
    const mapping = {
      'personal_info': 'personalInfo',
      'emergency_contact': 'emergencyContact',
      'health_info': 'healthInfo',
      'parent_guardian': 'parentGuardian',
      'soccer_experience': 'soccerExperience',
      'medical_clearance': 'medicalClearance'
    };
    return mapping[sectionId] || 'customFields';
  };

  // Helper function to get form field key from template field ID
  const getFormFieldKey = (templateFieldId) => {
    const fieldMappings = {
      // Emergency Contact fields
      'emergencyName': 'name',
      'emergencyRelationship': 'relationship', 
      'emergencyPhone': 'phone',
      // Personal Info fields
      'firstName': 'firstName',
      'lastName': 'lastName',
      'email': 'email',
      'phone': 'phone',
      'dateOfBirth': 'dateOfBirth',
      // Parent/Guardian fields
      'parentName': 'parentName',
      'parentEmail': 'parentEmail',
      'parentPhone': 'parentPhone',
      // Soccer Experience fields
      'experienceLevel': 'experienceLevel',
      'preferredPosition': 'preferredPosition',
      'yearsPlaying': 'yearsPlaying',
      // Health Info fields
      'allergies': 'allergies',
      'medications': 'medications',
      'medicalConditions': 'medicalConditions',
      // Custom fields - use their original IDs as they're unique
      'custom_1757316988142': 'custom_1757316988142', // Blood Group
      'custom_1757277699491': 'custom_1757277699491', // Buddy Request
      'custom_1757317005316': 'custom_1757317005316', // Any other Information
      'custom_1757317022087': 'custom_1757317022087', // Living with Parents
      'custom_1757317064381': 'custom_1757317064381'  // Soccer Experience
    };
    return fieldMappings[templateFieldId] || templateFieldId;
  };

  // Render document upload step dynamically based on template
  const renderDocumentUploadStep = () => {
    if (!template?.documentRequirements) {
      return (
        <div className={styles.stepContent}>
          <h3>📎 Document Upload</h3>
          <p className={styles.stepDescription}>No documents required for this program.</p>
        </div>
      );
    }

    return (
      <div className={styles.stepContent}>
        <h3>📎 Document Upload</h3>
        <p className={styles.stepDescription}>
          Please upload the required documents. Make sure files are clear and readable.
        </p>
        
        <div className={styles.documentsSection}>
          {template.documentRequirements.map(doc => (
            <div key={doc.id} className={styles.documentRequirement}>
              <div className={styles.docHeader}>
                <h4>{getDocumentIcon(doc.id)} {doc.name}</h4>
                {doc.required && <span className={styles.required}>Required</span>}
              </div>
              <p>{doc.description}</p>
              <input
                type="file"
                accept={doc.acceptedFormats?.map(format => `.${format}`).join(',') || '.pdf,.jpg,.jpeg,.png,.doc,.docx'}
                onChange={(e) => {
                  const file = e.target.files[0];
                  if (file) {
                    try {
                      validateFile(file);
                      setUploadedDocuments(prev => ({
                        ...prev,
                        [doc.id]: file
                      }));
                    } catch (error) {
                      alert(`${doc.name} upload error: ${error.message}`);
                      e.target.value = '';
                    }
                  }
                }}
              />
              {uploadedDocuments[doc.id] && (
                <div className={styles.uploadedFile}>
                  ✅ {uploadedDocuments[doc.id].name}
                  <button
                    type="button"
                    onClick={() => setUploadedDocuments(prev => {
                      const newDocs = {...prev};
                      delete newDocs[doc.id];
                      return newDocs;
                    })}
                    className={styles.removeFile}
                  >
                    ✖
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    );
  };

  // Helper function to get document icons
  const getDocumentIcon = (docId) => {
    const icons = {
      'birth_certificate': '📄',
      'medical_clearance': '🏥',
      'photo_id': '🆔',
      'insurance_info': '🛡️'
    };
    return icons[docId] || '📎';
  };

  // Render review step dynamically
  const renderReviewStep = () => {
    return (
      <div className={styles.stepContent}>
        <h3>✅ Review & Submit</h3>
        <p className={styles.stepDescription}>
          Please review your information before submitting your registration application.
        </p>
        
        <div className={styles.reviewSection}>
          {(() => {
            // Deduplicate sections by section ID to prevent duplicate rendering
            const uniqueSections = [];
            const seenSectionIds = new Set();
            
            steps.filter(step => step.section).forEach(step => {
              if (!seenSectionIds.has(step.section.id)) {
                seenSectionIds.add(step.section.id);
                uniqueSections.push(step);
                // Added unique section to review
              } else {
                // Skipped duplicate section in review
              }
            });
            
            return uniqueSections.map(step => {
              const sectionKey = getSectionKey(step.section.id);
              const sectionData = formData[sectionKey];
              
              return (
                <div key={step.section.id} className={styles.reviewItem}>
                  <h4>{step.name}</h4>
                  <div className={styles.reviewData}>
                    {step.section.fields?.map(field => {
                      const formFieldKey = getFormFieldKey(field.id);
                      const value = sectionData?.[formFieldKey];
                      if (!value) return null;
                      
                      return (
                        <p key={field.id}>
                          <strong>{field.label}:</strong> {value}
                        </p>
                      );
                    })}
                  </div>
                  <button 
                    className={styles.editButton}
                    onClick={() => setCurrentStep(step.id)}
                  >
                    Edit
                  </button>
                </div>
              );
            });
          })()}
          
          {/* Documents Review */}
          {template?.documentRequirements && template.documentRequirements.length > 0 && (
            <div className={styles.reviewItem}>
              <h4>Documents</h4>
              <div className={styles.reviewData}>
                {template.documentRequirements.map(doc => (
                  <p key={doc.id}>
                    <strong>{doc.name}:</strong> {uploadedDocuments[doc.id] ? '✅ Uploaded' : '❌ Missing'}
                  </p>
                ))}
              </div>
              <button 
                className={styles.editButton}
                onClick={() => setCurrentStep(steps.findIndex(s => s.sectionId === 'documents') + 1)}
              >
                Edit
              </button>
            </div>
          )}
        </div>
        
        <div className={styles.agreementSection}>
          <label className={styles.checkbox}>
            <input
              type="checkbox"
              checked={termsAccepted}
              onChange={(e) => setTermsAccepted(e.target.checked)}
              required
            />
            <span>I agree to the terms and conditions and liability waiver</span>
          </label>
        </div>
      </div>
    );
  };

  const renderStepContent = () => {
    const currentStepData = steps[currentStep - 1];
    
    if (!currentStepData) return null;

    // Handle special steps (documents and review)
    if (currentStepData.sectionId === 'documents') {
      return renderDocumentUploadStep();
    }
    
    if (currentStepData.sectionId === 'review') {
      return renderReviewStep();
    }

    // Render dynamic section based on template
    if (currentStepData.section) {
      const section = currentStepData.section;
      const sectionKey = getSectionKey(section.id);
      
      // Rendering dynamic section
      
      return (
        <div className={styles.stepContent}>
          <h3>{currentStepData.icon} {section.name}</h3>
          <p className={styles.stepDescription}>
            {section.description || `Please fill out the ${section.name.toLowerCase()} information.`}
          </p>
          
          <div className={styles.formGrid}>
            {section.fields?.map((field, index) => (
              <div key={`${sectionKey}-${field.id}`}>
                {renderFormField(field, sectionKey)}
              </div>
            ))}
          </div>
        </div>
      );
    }

    // Fallback to hardcoded content for backward compatibility
    switch (currentStep) {
      case 1:
        return (
          <div className={styles.stepContent}>
            <h3>👤 Personal Information</h3>
            <p className={styles.stepDescription}>
              We've pre-filled some information from your profile. Please review and complete any missing details.
            </p>
            
            <div className={styles.formGrid}>
              <div className={styles.inputGroup}>
                <label>First Name *</label>
                <input
                  type="text"
                  value={formData.personalInfo.firstName || ''}
                  onChange={(e) => handleInputChange('personalInfo', 'firstName', e.target.value)}
                  required
                />
              </div>
              
              <div className={styles.inputGroup}>
                <label>Last Name *</label>
                <input
                  type="text"
                  value={formData.personalInfo.lastName || ''}
                  onChange={(e) => handleInputChange('personalInfo', 'lastName', e.target.value)}
                  required
                />
              </div>
              
              <div className={styles.inputGroup}>
                <label>Email *</label>
                <input
                  type="email"
                  value={formData.personalInfo.email || ''}
                  onChange={(e) => handleInputChange('personalInfo', 'email', e.target.value)}
                  required
                />
              </div>
              
              <div className={styles.inputGroup}>
                <label>Phone *</label>
                <input
                  type="tel"
                  value={formData.personalInfo.phone || ''}
                  onChange={(e) => handleInputChange('personalInfo', 'phone', e.target.value)}
                  required
                />
              </div>
              
              <div className={styles.inputGroup}>
                <label>Date of Birth *</label>
                <input
                  type="date"
                  value={formData.personalInfo.dateOfBirth || ''}
                  onChange={(e) => handleInputChange('personalInfo', 'dateOfBirth', e.target.value)}
                  required
                />
              </div>
              
              <div className={styles.inputGroup}>
                <label>Address *</label>
                <input
                  type="text"
                  value={formData.personalInfo.address || ''}
                  onChange={(e) => handleInputChange('personalInfo', 'address', e.target.value)}
                  required
                />
              </div>
            </div>
          </div>
        );

      case 2:
        return (
          <div className={styles.stepContent}>
            <h3>🆘 Emergency Contact</h3>
            <p className={styles.stepDescription}>
              Please provide emergency contact information in case we need to reach someone on your behalf.
            </p>
            
            <div className={styles.formGrid}>
              <div className={styles.inputGroup}>
                <label>Contact Name *</label>
                <input
                  type="text"
                  value={formData.emergencyContact.name || ''}
                  onChange={(e) => handleInputChange('emergencyContact', 'name', e.target.value)}
                  required
                />
              </div>
              
              <div className={styles.inputGroup}>
                <label>Relationship *</label>
                <select
                  value={formData.emergencyContact.relationship || ''}
                  onChange={(e) => handleInputChange('emergencyContact', 'relationship', e.target.value)}
                  required
                >
                  <option value="">Select relationship</option>
                  <option value="Parent">Parent</option>
                  <option value="Guardian">Guardian</option>
                  <option value="Spouse">Spouse</option>
                  <option value="Sibling">Sibling</option>
                  <option value="Friend">Friend</option>
                  <option value="Other">Other</option>
                </select>
              </div>
              
              <div className={styles.inputGroup}>
                <label>Phone Number *</label>
                <input
                  type="tel"
                  value={formData.emergencyContact.phone || ''}
                  onChange={(e) => handleInputChange('emergencyContact', 'phone', e.target.value)}
                  required
                />
              </div>
              
              <div className={styles.inputGroup}>
                <label>Email</label>
                <input
                  type="email"
                  value={formData.emergencyContact.email || ''}
                  onChange={(e) => handleInputChange('emergencyContact', 'email', e.target.value)}
                />
              </div>
            </div>
          </div>
        );

      case 3:
        return (
          <div className={styles.stepContent}>
            <h3>🏥 Health Information</h3>
            <p className={styles.stepDescription}>
              Please provide any relevant health information for safety purposes.
            </p>
            
            <div className={styles.formSection}>
              <label>Medical Conditions</label>
              <div className={styles.checkboxGroup}>
                {['Asthma', 'Diabetes', 'Heart Condition', 'Allergies', 'Previous Injuries', 'None'].map(condition => (
                  <label key={condition} className={styles.checkbox}>
                    <input
                      type="checkbox"
                      checked={formData.healthInfo.conditions?.includes(condition) || false}
                      onChange={(e) => {
                        const conditions = formData.healthInfo.conditions || [];
                        if (e.target.checked) {
                          handleInputChange('healthInfo', 'conditions', [...conditions, condition]);
                        } else {
                          handleInputChange('healthInfo', 'conditions', conditions.filter(c => c !== condition));
                        }
                      }}
                    />
                    <span>{condition}</span>
                  </label>
                ))}
              </div>
            </div>
            
            <div className={styles.inputGroup}>
              <label>Allergies (if any)</label>
              <textarea
                value={formData.healthInfo.allergies || ''}
                onChange={(e) => handleInputChange('healthInfo', 'allergies', e.target.value)}
                placeholder="Please list any allergies..."
                rows={3}
              />
            </div>
            
            <div className={styles.inputGroup}>
              <label>Current Medications</label>
              <textarea
                value={formData.healthInfo.medications || ''}
                onChange={(e) => handleInputChange('healthInfo', 'medications', e.target.value)}
                placeholder="Please list any current medications..."
                rows={3}
              />
            </div>
          </div>
        );

      case 4:
        return (
          <div className={styles.stepContent}>
            <h3>📎 Document Upload</h3>
            <p className={styles.stepDescription}>
              Please upload the required documents. Make sure files are clear and readable.
            </p>
            
            <div className={styles.documentsSection}>
              <div className={styles.documentRequirement}>
                <div className={styles.docHeader}>
                  <h4>🆔 Photo ID/Passport</h4>
                  <span className={styles.required}>Required</span>
                </div>
                <p>Government-issued photo identification</p>
                <input
                  type="file"
                  accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                  onChange={(e) => {
                    const file = e.target.files[0];
                    if (file) {
                      try {
                        validateFile(file);
                        setUploadedDocuments(prev => ({
                          ...prev,
                          photoId: file
                        }));
                      } catch (error) {
                        alert(`Photo ID upload error: ${error.message}`);
                        e.target.value = ''; // Clear the input
                      }
                    }
                  }}
                />
                {uploadedDocuments.photoId && (
                  <div className={styles.uploadedFile}>
                    ✅ {uploadedDocuments.photoId.name}
                    <button
                      type="button"
                      onClick={() => setUploadedDocuments(prev => {
                        const newDocs = {...prev};
                        delete newDocs.photoId;
                        return newDocs;
                      })}
                      className={styles.removeFile}
                    >
                      ✖
                    </button>
                  </div>
                )}
              </div>
              
              <div className={styles.documentRequirement}>
                <div className={styles.docHeader}>
                  <h4>🏥 Medical Clearance</h4>
                  <span className={styles.required}>Required</span>
                </div>
                <p>Doctor certification for sports participation (must be dated within 6 months)</p>
                <input
                  type="file"
                  accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                  onChange={(e) => {
                    const file = e.target.files[0];
                    if (file) {
                      try {
                        validateFile(file);
                        setUploadedDocuments(prev => ({
                          ...prev,
                          medicalClearance: file
                        }));
                      } catch (error) {
                        alert(`Medical clearance upload error: ${error.message}`);
                        e.target.value = ''; // Clear the input
                      }
                    }
                  }}
                />
                {uploadedDocuments.medicalClearance && (
                  <div className={styles.uploadedFile}>
                    ✅ {uploadedDocuments.medicalClearance.name}
                    <button
                      type="button"
                      onClick={() => setUploadedDocuments(prev => {
                        const newDocs = {...prev};
                        delete newDocs.medicalClearance;
                        return newDocs;
                      })}
                      className={styles.removeFile}
                    >
                      ✖
                    </button>
                  </div>
                )}
              </div>
              
              <div className={styles.documentRequirement}>
                <div className={styles.docHeader}>
                  <h4>🛡️ Insurance Card</h4>
                  <span className={styles.required}>Required</span>
                </div>
                <p>Health insurance verification (both front and back)</p>
                <input
                  type="file"
                  accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                  multiple
                  onChange={(e) => {
                    const files = Array.from(e.target.files);
                    if (files.length > 0) {
                      try {
                        // Validate each file
                        files.forEach(file => validateFile(file));
                        
                        if (files.length > 2) {
                          throw new Error('Maximum 2 insurance cards allowed.');
                        }
                        
                        setUploadedDocuments(prev => ({
                          ...prev,
                          insuranceCards: files
                        }));
                      } catch (error) {
                        alert(`Insurance cards upload error: ${error.message}`);
                        e.target.value = ''; // Clear the input
                      }
                    }
                  }}
                />
                {uploadedDocuments.insuranceCards && uploadedDocuments.insuranceCards.length > 0 && (
                  <div className={styles.uploadedFiles}>
                    {uploadedDocuments.insuranceCards.map((file, index) => (
                      <div key={index} className={styles.uploadedFile}>
                        ✅ {file.name}
                        <button
                          type="button"
                          onClick={() => setUploadedDocuments(prev => ({
                            ...prev,
                            insuranceCards: prev.insuranceCards.filter((_, i) => i !== index)
                          }))}
                          className={styles.removeFile}
                        >
                          ✖
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        );

      case 5:
        // Use dynamic review step instead of hardcoded fallback
        return renderReviewStep();

      default:
        return null;
    }
  };

  return createPortal(
    <>
      <div className={styles.wizardOverlay} onClick={handleOverlayClick}>
        <div className={styles.wizard} onClick={(e) => e.stopPropagation()}>
          <div className={styles.wizardHeader}>
            <h2>Program Registration</h2>
            <button className={styles.closeButton} onClick={onClose}>✕</button>
          </div>

        {/* Progress Steps */}
        <div className={styles.stepsContainer}>
          {steps.map((step) => {
            const isCompleted = step.id < currentStep && isStepComplete(step.id);
            const isActive = currentStep === step.id;
            const hasErrors = step.id < currentStep && !isStepComplete(step.id);
            
            return (
              <div
                key={step.id}
                className={`${styles.step} ${
                  isActive ? styles.activeStep : ''
                } ${isCompleted ? styles.completedStep : ''} ${
                  hasErrors ? styles.errorStep : ''
                }`}
              >
                <div className={styles.stepIcon}>
                  {isCompleted ? '✅' : hasErrors ? '❌' : step.icon}
                </div>
                <div className={styles.stepName}>{step.name}</div>
                <div className={styles.stepNumber}>{step.id}</div>
              </div>
            );
          })}
        </div>

        {/* Step Content */}
        <div className={styles.wizardContent}>
          {renderStepContent()}
        </div>

        {/* Navigation */}
        <div className={styles.wizardFooter}>
          <button
            className={styles.backButton}
            onClick={handlePrevious}
            disabled={currentStep === 1}
          >
            ← Previous
          </button>
          
          <div className={styles.stepIndicator}>
            Step {currentStep} of {steps.length}
          </div>
          
          {currentStep < steps.length ? (
            <button
              className={styles.nextButton}
              onClick={handleNext}
            >
              Next →
            </button>
          ) : (
            <button
              className={styles.submitButton}
              onClick={handleSubmit}
              disabled={isSubmitting || !isFormComplete()}
            >
              {isSubmitting ? 'Submitting...' : !isFormComplete() ? 'Complete Required Fields' : 'Submit Application'}
            </button>
          )}
        </div>
      </div>
    </div>
      
    {/* Loading Overlay */}
    <LoadingOverlay 
      isVisible={isSubmitting}
      message="Submitting your application..."
      showSpinner={true}
    />
    </>,
    document.body
  );
};

export default RegistrationWizard;
